import {
  type CollapsibleGraphGroup,
  isNode,
  isComponentGraphEdgeModel,
  isAggregatedGraphEdge,
  isCollapsibleGroup,
} from '@ardoq/graph';
import { dispatchAction } from '@ardoq/rxbeach';
import {
  IEdge,
  INode,
  Rect,
  type GraphComponent,
  type GraphInputMode,
  type IModelItem,
  type ItemClickedEventArgs,
} from '@ardoq/yfiles';
import { type ViewIds } from '@ardoq/api-types';
import { BaseNode as OldGraphNode } from 'graph/BaseNode';
import { dataModelId } from '../graphComponentUtil';
import { Edge } from 'graph/edge';
import {
  type GraphGroupCollapseState,
  type GraphItemContextMenuTarget,
  graphItemContextMenuRequested,
} from './actions';

const toZoomToNode = (
  graphComponent: GraphComponent,
  node: INode
): VoidFunction => {
  const {
    topLeft: { x, y },
    width,
    height,
  } = node.layout;
  return () => graphComponent.zoomToAnimated(new Rect(x, y, width, height));
};

const toGroupCollapseState = ({ tag }: IModelItem): GraphGroupCollapseState => {
  const { id, collapsed }: CollapsibleGraphGroup = tag;
  return { id, collapsed };
};

const getSelectedGroups = ({
  graph,
  selection,
}: GraphComponent): GraphGroupCollapseState[] | null => {
  const selectedItems = Array.from(selection);
  const isSelectionAllGroups = selectedItems.every(
    selectionItem =>
      selectionItem instanceof INode && graph.isGroupNode(selectionItem)
  );
  if (!isSelectionAllGroups) {
    return null;
  }
  return selectedItems.map(toGroupCollapseState);
};

const toNodeTarget = (
  graphComponent: GraphComponent,
  node: INode
): GraphItemContextMenuTarget | null => {
  const { tag: graphItem } = node;
  if (isNode(graphItem) || graphItem instanceof OldGraphNode) {
    return {
      type: 'node',
      modelId: dataModelId(graphItem),
      nodeId: graphItem.id,
      zoomToItem: toZoomToNode(graphComponent, node),
    };
  }
  if (isCollapsibleGroup(graphItem)) {
    return {
      type: 'group',
      modelId: graphItem.modelId,
      groupId: graphItem.id,
      collapsed: graphItem.collapsed,
      zoomToItem: toZoomToNode(graphComponent, node),
      selectedGroups: getSelectedGroups(graphComponent),
    };
  }
  return null;
};

const toEdgeTarget = ({
  tag: graphItem,
}: IEdge): GraphItemContextMenuTarget | null => {
  if (isComponentGraphEdgeModel(graphItem) || graphItem instanceof Edge) {
    const modelId = dataModelId(graphItem);
    return {
      type: 'edge',
      referenceIds: [modelId],
      eventTargetModelId: modelId,
    };
  }
  if (isAggregatedGraphEdge(graphItem)) {
    return {
      type: 'edge',
      referenceIds: graphItem.modelIds,
      eventTargetModelId: '',
    };
  }
  return null;
};

const toContextMenuTarget = (
  graphComponent: GraphComponent,
  item: IModelItem
): GraphItemContextMenuTarget | null => {
  if (item instanceof INode) {
    return toNodeTarget(graphComponent, item);
  }
  if (item instanceof IEdge) {
    return toEdgeTarget(item);
  }
  return null;
};

type GraphViewpointModeItemRightClickedListenerArgs = {
  graphComponent: GraphComponent;
  isViewpointMode: () => boolean;
  viewId: ViewIds;
  viewInstanceId: string;
};
const graphItemRightClickedListener =
  ({
    graphComponent,
    isViewpointMode,
    viewId,
    viewInstanceId,
  }: GraphViewpointModeItemRightClickedListenerArgs) =>
  (e: ItemClickedEventArgs<IModelItem>, _: GraphInputMode) => {
    const target = toContextMenuTarget(graphComponent, e.item);
    if (!target) {
      return;
    }
    const { x, y } = e.location;
    const { x: left, y: top } = graphComponent.viewToPageCoordinates(
      graphComponent.worldToViewCoordinates([x, y])
    );
    dispatchAction(
      graphItemContextMenuRequested({
        target,
        position: { left, top },
        isViewpointMode: isViewpointMode(),
        viewId,
        viewInstanceId,
      })
    );
    e.handled = true;
  };

export { graphItemRightClickedListener };
